import { useRef, useState, useCallback, useEffect, useLayoutEffect, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';
import type { NotableSegment } from '../types/videoEdit';

export interface RecordingSegment {
  id: string;
  startTime: number;
  endTime: number;
  audioBlob?: Blob; // Audio recording for this segment
  canvasBlob?: Blob; // Canvas overlay recording for this segment
}

export interface AnnotationSnapshot {
  id: string;
  timestamp: number;  // Start time
  endTime: number;    // End time (draggable)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  annotations: any[];
  canvasBlob?: Blob;
}

export interface AudioRecording {
  id: string;
  timestamp: number;
  audioBlob?: Blob;
}

export interface ClipMask {
  id: string;
  startTime: number;
  endTime: number;
}

interface TimelineProps {
  duration: number;
  currentTime: number;
  recordingSegments?: RecordingSegment[];
  annotationSnapshots?: AnnotationSnapshot[];
  audioRecordings?: AudioRecording[];
  clipMasks?: ClipMask[];
  clipMaskFirstBound?: number | null;
  isRecording?: boolean;
  isPausedAudioRecording?: boolean;
  isPlaying?: boolean;
  onSeek: (time: number) => void;
  /* Fired on the drag transitions only, so the owner can pause playback while
     a scrub is in flight and resume it after. Timeline does not pause anything
     itself: it does not own the video, and what "was playing" means belongs to
     whoever does. Optional so every existing caller is unaffected. */
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
  onTogglePlay?: () => void;
  onDeleteSegment?: (segmentId: string) => void;
  onDeleteSnapshot?: (snapshotId: string) => void;
  onDeleteAudioRecording?: (recordingId: string) => void;
  onDeleteClipMask?: (maskId: string) => void;
  onUpdateClipMask?: (maskId: string, newStartTime: number, newEndTime: number) => void;
  onUpdateSnapshotEndTime?: (snapshotId: string, newEndTime: number) => void;
  slowMotionSegments?: NotableSegment[];
  sloMoFirstBound?: number | null;
  onUpdateSloMoSegmentEndTime?: (segmentId: string, newEndTime: number) => void;
  onSaveSegmentToLibrary?: (seg: NotableSegment) => void;
  isSloMoRecording?: boolean;
  playIntervals?: Array<{ startTime: number; endTime: number }>;
  onCreateHighlightFromInterval?: (startTime: number, endTime: number) => void;
  hidePlayButton?: boolean;
  className?: string;
}

export function Timeline({
  duration,
  currentTime,
  // recordingSegments = [],
  // annotationSnapshots = [],
  // audioRecordings = [],
  // clipMasks = [],
  // clipMaskFirstBound = null,
  // isRecording = false,
  // isPausedAudioRecording = false,
  isPlaying = false,
  onSeek,
  onScrubStart,
  onScrubEnd,
  onTogglePlay,
  // onDeleteSegment,
  // onDeleteSnapshot,
  // onDeleteAudioRecording,
  // onDeleteClipMask,
  // onUpdateClipMask,
  // onUpdateSnapshotEndTime,
  slowMotionSegments = [],
  sloMoFirstBound = null,
  onUpdateSloMoSegmentEndTime,
  onSaveSegmentToLibrary,
  // isSloMoRecording = false,
  playIntervals = [],
  onCreateHighlightFromInterval,
  className = '',
}: TimelineProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [timelineWidth, setTimelineWidth] = useState(0);

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setTimelineWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const seekRafRef = useRef<number | null>(null);
  const pendingSeekTimeRef = useRef<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // const [draggingSnapshotId, setDraggingSnapshotId] = useState<string | null>(null);
  // const [draggingClipMask, setDraggingClipMask] = useState<{ id: string; edge: 'start' | 'end' } | null>(null);
  const [draggingSloMoSegmentId, setDraggingSloMoSegmentId] = useState<string | null>(null);

  // Mobile: track which timeline item is "tapped" to show its hover UI (tooltip + delete)
  const [tappedItemId, setTappedItemId] = useState<string | null>(null);
  const phone = usePhoneLayout().phone;

  // Dismiss tapped item when tapping elsewhere
  useEffect(() => {
    if (!tappedItemId) return;
    const dismiss = () => setTappedItemId(null);
    // Use a timeout so the current tap event doesn't immediately dismiss
    const id = setTimeout(() => document.addEventListener('touchstart', dismiss, { once: true }), 50);
    return () => { clearTimeout(id); document.removeEventListener('touchstart', dismiss); };
  }, [tappedItemId]);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate playhead position percentage
  const playheadPosition = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Handle timeline click/drag
  const handleTimelineInteraction = (e: MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percent * duration;
    onSeek(newTime);
  };

  // Throttled seek: batches rapid seek calls into one per animation frame
  const throttledSeek = (time: number) => {
    pendingSeekTimeRef.current = time;
    if (seekRafRef.current !== null) return; // Already scheduled
    seekRafRef.current = requestAnimationFrame(() => {
      seekRafRef.current = null;
      if (pendingSeekTimeRef.current !== null) {
        onSeek(pendingSeekTimeRef.current);
        pendingSeekTimeRef.current = null;
      }
    });
  };

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    onScrubStart?.();
    handleTimelineInteraction(e);

    const handleGlobalMouseMove = (globalE: globalThis.MouseEvent) => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = globalE.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, clickX / rect.width));
      const newTime = percent * duration;
      throttledSeek(newTime);
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      onScrubEnd?.();
      // Flush any pending seek on release
      if (seekRafRef.current !== null) {
        cancelAnimationFrame(seekRafRef.current);
        seekRafRef.current = null;
      }
      if (pendingSeekTimeRef.current !== null) {
        onSeek(pendingSeekTimeRef.current);
        pendingSeekTimeRef.current = null;
      }
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      handleTimelineInteraction(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    /* Fires alongside handleGlobalMouseUp when the release lands on the
       timeline itself. useScrubPause's end is idempotent, so the second call
       is a no-op rather than a second resume. */
    onScrubEnd?.();
  };

  // Touch: timeline scrubbing (mirrors handleMouseDown)
  const startTouchScrub = useCallback((clientX: number) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = percent * duration;
    setIsDragging(true);
    onScrubStart?.();
    onSeek(newTime);

    const handleGlobalTouchMove = (globalE: TouchEvent) => {
      globalE.preventDefault(); // non-passive since we add it imperatively
      if (!timelineRef.current) return;
      const t = globalE.touches[0];
      const r = timelineRef.current.getBoundingClientRect();
      const p = Math.max(0, Math.min(1, (t.clientX - r.left) / r.width));
      throttledSeek(p * duration);
    };

    const handleGlobalTouchEnd = () => {
      setIsDragging(false);
      onScrubEnd?.();
      if (seekRafRef.current !== null) {
        cancelAnimationFrame(seekRafRef.current);
        seekRafRef.current = null;
      }
      if (pendingSeekTimeRef.current !== null) {
        onSeek(pendingSeekTimeRef.current);
        pendingSeekTimeRef.current = null;
      }
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };

    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);
  }, [duration, onSeek, throttledSeek, onScrubStart, onScrubEnd]);

  const handleTouchDown = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    startTouchScrub(e.touches[0].clientX);
  }, [startTouchScrub]);

  /**
   * Touch on a segment bar (spec 12.3): a tap still toggles the bar's label,
   * but a finger that moves more than 8 first is a drag, and a drag scrubs
   * the playhead as it would from the bare track. The label goes back to how
   * it was. The mouse never reaches this: the bar has no mouse handler, so a
   * press on it already scrubs through the track.
   */
  const handleBarTouchDrag = useCallback((e: React.TouchEvent, undoTap: () => void) => {
    const start = e.touches[0];
    const x0 = start.clientX, y0 = start.clientY;
    const move = (m: TouchEvent) => {
      const t = m.touches[0];
      if (!t || Math.hypot(t.clientX - x0, t.clientY - y0) <= 8) return;
      m.preventDefault();
      cleanup();
      undoTap();
      startTouchScrub(t.clientX);
    };
    const cleanup = () => {
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', cleanup);
      document.removeEventListener('touchcancel', cleanup);
    };
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', cleanup);
    document.addEventListener('touchcancel', cleanup);
  }, [startTouchScrub]);

  // Touch: annotation snapshot drag (mirrors handleAnnotationDragStart)
  // const handleAnnotationTouchStart = useCallback((e: React.TouchEvent, snapshotId: string) => {
  //   e.stopPropagation();
  //   setDraggingSnapshotId(snapshotId);

  //   const handleDrag = (moveE: TouchEvent) => {
  //     moveE.preventDefault();
  //     if (!timelineRef.current || !onUpdateSnapshotEndTime) return;
  //     const touch = moveE.touches[0];
  //     const rect = timelineRef.current.getBoundingClientRect();
  //     const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
  //     const newEndTime = percent * duration;

  //     const snapshot = annotationSnapshots.find(s => s.id === snapshotId);
  //     if (snapshot && newEndTime >= snapshot.timestamp + 0.5) {
  //       onUpdateSnapshotEndTime(snapshotId, newEndTime);
  //     }
  //   };

  //   const handleDragEnd = () => {
  //     setDraggingSnapshotId(null);
  //     document.removeEventListener('touchmove', handleDrag);
  //     document.removeEventListener('touchend', handleDragEnd);
  //   };

  //   document.addEventListener('touchmove', handleDrag, { passive: false });
  //   document.addEventListener('touchend', handleDragEnd);
  // }, [duration, annotationSnapshots, onUpdateSnapshotEndTime]);

  // Touch: clip mask drag (mirrors handleClipMaskDragStart)
  // const handleClipMaskTouchStart = useCallback((e: React.TouchEvent, maskId: string, edge: 'start' | 'end') => {
  //   e.stopPropagation();
  //   setDraggingClipMask({ id: maskId, edge });

  //   const handleDrag = (moveE: TouchEvent) => {
  //     moveE.preventDefault();
  //     if (!timelineRef.current || !onUpdateClipMask) return;
  //     const touch = moveE.touches[0];
  //     const rect = timelineRef.current.getBoundingClientRect();
  //     const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
  //     const newTime = percent * duration;

  //     const mask = clipMasks.find(m => m.id === maskId);
  //     if (!mask) return;

  //     const sortedMasks = [...clipMasks].sort((a, b) => a.startTime - b.startTime);
  //     const maskIndex = sortedMasks.findIndex(m => m.id === maskId);

  //     if (edge === 'start') {
  //       const prevMask = maskIndex > 0 ? sortedMasks[maskIndex - 1] : null;
  //       const lowerBound = prevMask ? prevMask.endTime : 0;
  //       const upperBound = mask.endTime - 0.5;
  //       const clampedTime = Math.max(lowerBound, Math.min(upperBound, newTime));
  //       onUpdateClipMask(maskId, clampedTime, mask.endTime);
  //     } else {
  //       const nextMask = maskIndex < sortedMasks.length - 1 ? sortedMasks[maskIndex + 1] : null;
  //       const lowerBound = mask.startTime + 0.5;
  //       const upperBound = nextMask ? nextMask.startTime : duration;
  //       const clampedTime = Math.max(lowerBound, Math.min(upperBound, newTime));
  //       onUpdateClipMask(maskId, mask.startTime, clampedTime);
  //     }
  //   };

  //   const handleDragEnd = () => {
  //     setDraggingClipMask(null);
  //     document.removeEventListener('touchmove', handleDrag);
  //     document.removeEventListener('touchend', handleDragEnd);
  //   };

  //   document.addEventListener('touchmove', handleDrag, { passive: false });
  //   document.addEventListener('touchend', handleDragEnd);
  // }, [duration, clipMasks, onUpdateClipMask]);

  // Touch: slo-mo segment right-edge drag
  const handleSloMoSegmentTouchStart = useCallback((e: React.TouchEvent, segmentId: string) => {
    e.stopPropagation();
    setDraggingSloMoSegmentId(segmentId);

    const handleDrag = (moveE: TouchEvent) => {
      moveE.preventDefault();
      if (!timelineRef.current || !onUpdateSloMoSegmentEndTime) return;
      const touch = moveE.touches[0];
      const rect = timelineRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
      const newEndTime = percent * duration;

      const seg = slowMotionSegments.find(s => s.id === segmentId);
      if (seg && newEndTime >= seg.startTime + 0.5) {
        onUpdateSloMoSegmentEndTime(segmentId, newEndTime);
      }
    };

    const handleDragEnd = () => {
      setDraggingSloMoSegmentId(null);
      document.removeEventListener('touchmove', handleDrag);
      document.removeEventListener('touchend', handleDragEnd);
    };

    document.addEventListener('touchmove', handleDrag, { passive: false });
    document.addEventListener('touchend', handleDragEnd);
  }, [duration, slowMotionSegments, onUpdateSloMoSegmentEndTime]);

  // Handle dragging slo-mo segment end time (right edge only)
  const handleSloMoSegmentDragStart = (e: React.MouseEvent, segmentId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingSloMoSegmentId(segmentId);

    const handleDrag = (moveE: globalThis.MouseEvent) => {
      if (!timelineRef.current || !onUpdateSloMoSegmentEndTime) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (moveE.clientX - rect.left) / rect.width));
      const newEndTime = percent * duration;

      const seg = slowMotionSegments.find(s => s.id === segmentId);
      if (seg && newEndTime >= seg.startTime + 0.5) {
        onUpdateSloMoSegmentEndTime(segmentId, newEndTime);
      }
    };

    const handleDragEnd = () => {
      setDraggingSloMoSegmentId(null);
      document.removeEventListener('mousemove', handleDrag);
      document.removeEventListener('mouseup', handleDragEnd);
    };

    document.addEventListener('mousemove', handleDrag);
    document.addEventListener('mouseup', handleDragEnd);
  };

  // Handle dragging annotation snapshot end time
  // const handleAnnotationDragStart = (e: React.MouseEvent, snapshotId: string) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   setDraggingSnapshotId(snapshotId);

  //   const handleDrag = (moveE: globalThis.MouseEvent) => {
  //     if (!timelineRef.current || !onUpdateSnapshotEndTime) return;
  //     const rect = timelineRef.current.getBoundingClientRect();
  //     const percent = Math.max(0, Math.min(1, (moveE.clientX - rect.left) / rect.width));
  //     const newEndTime = percent * duration;

  //     // Find the snapshot and ensure endTime >= timestamp (minimum 0.5s duration)
  //     const snapshot = annotationSnapshots.find(s => s.id === snapshotId);
  //     if (snapshot && newEndTime >= snapshot.timestamp + 0.5) {
  //       onUpdateSnapshotEndTime(snapshotId, newEndTime);
  //     }
  //   };

  //   const handleDragEnd = () => {
  //     setDraggingSnapshotId(null);
  //     document.removeEventListener('mousemove', handleDrag);
  //     document.removeEventListener('mouseup', handleDragEnd);
  //   };

  //   document.addEventListener('mousemove', handleDrag);
  //   document.addEventListener('mouseup', handleDragEnd);
  // };

  // Handle dragging clip mask start/end time
  // const handleClipMaskDragStart = (e: React.MouseEvent, maskId: string, edge: 'start' | 'end') => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   setDraggingClipMask({ id: maskId, edge });

  //   const handleDrag = (moveE: globalThis.MouseEvent) => {
  //     if (!timelineRef.current || !onUpdateClipMask) return;
  //     const rect = timelineRef.current.getBoundingClientRect();
  //     const percent = Math.max(0, Math.min(1, (moveE.clientX - rect.left) / rect.width));
  //     const newTime = percent * duration;

  //     const mask = clipMasks.find(m => m.id === maskId);
  //     if (!mask) return;

  //     // Sort clip masks to find adjacent ones for bounds checking
  //     const sortedMasks = [...clipMasks].sort((a, b) => a.startTime - b.startTime);
  //     const maskIndex = sortedMasks.findIndex(m => m.id === maskId);

  //     if (edge === 'start') {
  //       // Left edge: can't go before previous mask's end or past own end (min 0.5s)
  //       const prevMask = maskIndex > 0 ? sortedMasks[maskIndex - 1] : null;
  //       const lowerBound = prevMask ? prevMask.endTime : 0;
  //       const upperBound = mask.endTime - 0.5;
  //       const clampedTime = Math.max(lowerBound, Math.min(upperBound, newTime));
  //       onUpdateClipMask(maskId, clampedTime, mask.endTime);
  //     } else {
  //       // Right edge: can't go past next mask's start or before own start (min 0.5s)
  //       const nextMask = maskIndex < sortedMasks.length - 1 ? sortedMasks[maskIndex + 1] : null;
  //       const lowerBound = mask.startTime + 0.5;
  //       const upperBound = nextMask ? nextMask.startTime : duration;
  //       const clampedTime = Math.max(lowerBound, Math.min(upperBound, newTime));
  //       onUpdateClipMask(maskId, mask.startTime, clampedTime);
  //     }
  //   };

  //   const handleDragEnd = () => {
  //     setDraggingClipMask(null);
  //     document.removeEventListener('mousemove', handleDrag);
  //     document.removeEventListener('mouseup', handleDragEnd);
  //   };

  //   document.addEventListener('mousemove', handleDrag);
  //   document.addEventListener('mouseup', handleDragEnd);
  // };

  /* Phone (spec 13.4): the one segment label, shown only while a finger is
     dragging: the end of the segment being dragged, or the segment the
     playhead is in during a scrub. */
  const phoneTipSeg = !phone || duration === 0 ? null
    : draggingSloMoSegmentId
      ? slowMotionSegments.find((sg) => sg.id === draggingSloMoSegmentId) ?? null
      : isDragging
        ? slowMotionSegments.find((sg) => currentTime >= sg.startTime && currentTime <= sg.endTime) ?? null
        : null;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {phoneTipSeg && (
        <SegmentDragTip
          trackRef={timelineRef}
          centrePercent={((phoneTipSeg.startTime + phoneTipSeg.endTime) / 2 / duration) * 100}
          text={`${phoneTipSeg.label}: ${formatTime(phoneTipSeg.startTime)} – ${formatTime(phoneTipSeg.endTime)}`}
        />
      )}
      {/* Play/Pause button */}
      <button
        onClick={(e) => { e.stopPropagation(); if (onTogglePlay) onTogglePlay(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-[rgba(255,255,255,0.08)] transition-colors"
        style={{ color: 'var(--editor-text)' }}
        type="button"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg>
        ) : (
          <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
        )}
      </button>

      {/* Time display */}
      {/* Sized to its own width so "0:12 / 1:30" never breaks; nothing is cut. */}
      <span className="w-max shrink-0 text-[13px] font-mono tabular-nums" style={{ color: 'var(--editor-text)' }}>
        {formatTime(currentTime)}
        <span style={{ color: 'var(--editor-text-muted)' }}> / {formatTime(duration)}</span>
      </span>

      {/* Scrubber track */}
      <div
        ref={timelineRef}
        className="relative flex-1 min-w-0 cursor-pointer select-none touch-none"
        style={{ height: '24px' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchDown}
      >
        {/* Track background */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[12px] rounded-full" style={{ background: 'var(--editor-border)' }} />

        {/* Track fill (played portion) */}
        <div
          className="absolute top-1/2 -translate-y-1/2 left-0 h-[12px] rounded-full"
          style={{ width: `${playheadPosition}%`, background: 'rgba(255, 255, 255, 0.12)' }}
        />
        {/* Annotation Snapshots - Duration bars (top row) with draggable end time */}
        {/* {annotationSnapshots.map((snapshot) => {
          if (duration === 0) return null;
          const startPercent = (snapshot.timestamp / duration) * 100;
          const widthPercent = ((snapshot.endTime - snapshot.timestamp) / duration) * 100;
          const isDraggingThis = draggingSnapshotId === snapshot.id;

          const isTapped = tappedItemId === `snapshot-${snapshot.id}`;
          return (
            <div
              key={snapshot.id}
              className={`absolute top-1 h-4 bg-blue-500/60 group z-20 rounded-sm border border-blue-600 ${isDraggingThis ? 'ring-2 ring-blue-400' : ''}`}
              style={{
                left: `${startPercent}%`,
                width: `${Math.max(widthPercent, 1)}%`, // Minimum 1% width for visibility
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                setTappedItemId(isTapped ? null : `snapshot-${snapshot.id}`);
              }}
            >
              {/ Drag handle on right edge /}
              <div
                className="absolute right-0 top-0 bottom-0 w-2 bg-blue-700 cursor-ew-resize hover:bg-blue-800 rounded-r-sm touch-none"
                onMouseDown={(e) => handleAnnotationDragStart(e, snapshot.id)}
                onTouchStart={(e) => handleAnnotationTouchStart(e, snapshot.id)}
                title="Drag to adjust duration"
              />

              {/ Tooltip on hover (+ tap on mobile) /}
              <div className={`absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-white text-gray-800 text-xs px-2 py-1 rounded shadow-lg border border-gray-300 whitespace-nowrap z-30 pointer-events-none ${isTapped ? 'block' : 'hidden group-hover:block'}`}>
                Annotation: {formatTime(snapshot.timestamp)} - {formatTime(snapshot.endTime)}
              </div>

              {/ Delete button on hover (+ tap on mobile) /}
              {onDeleteSnapshot && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSnapshot(snapshot.id);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  className={`absolute -top-1 -left-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs font-bold items-center justify-center hover:bg-red-600 z-30 ${isTapped ? 'flex' : 'hidden group-hover:flex'}`}
                  title="Delete annotation"
                >
                  ×
                </button>
              )}
            </div>
          );
        })} */}

        {/* Play intervals (RallyClip) — amber pills at z-10, below highlight segments */}
        {playIntervals.map((interval, index) => {
          if (duration === 0) return null;
          const startPercent = (interval.startTime / duration) * 100;
          const widthPercent = ((interval.endTime - interval.startTime) / duration) * 100;
          const itemId = `play-interval-${index}`;
          const isTapped = tappedItemId === itemId;
          const centrePercent = startPercent + Math.max(widthPercent, 0.5) / 2;
          const nearLeft = centrePercent < 12;
          const nearRight = centrePercent > 88;
          const ARROW_INSET = 12;
          return (
            <div
              key={index}
              className="absolute top-1/2 -translate-y-1/2 h-[8px] group z-10 rounded-full"
              style={{
                left: `${Math.max(0, Math.min(startPercent, 100 - Math.max(widthPercent, 0.5)))}%`,
                width: `${Math.max(widthPercent, 0.5)}%`,
                background: 'rgba(245, 158, 11, 0.45)',
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                setTappedItemId(isTapped ? null : itemId);
              }}
            >
              {/* Hover popup with "Create highlight" action */}
              {onCreateHighlightFromInterval && (
                <div
                  className={`absolute bottom-full z-30 ${isTapped ? 'block' : 'hidden group-hover:block'}`}
                  style={{
                    marginBottom: 8,
                    left: '50%',
                    transform: nearLeft
                      ? `translateX(-${ARROW_INSET}px)`
                      : nearRight
                        ? `translateX(calc(-100% + ${ARROW_INSET}px))`
                        : 'translateX(-50%)',
                  }}
                >
                  <div
                    className="relative w-max max-w-[240px] text-[var(--editor-text)] text-xs px-2 py-1.5 rounded shadow-lg flex flex-col gap-1 [overflow-wrap:anywhere]"
                    style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}
                  >
                    <span style={{ color: 'var(--editor-text-muted)' }}>
                      {formatTime(interval.startTime)} – {formatTime(interval.endTime)}
                    </span>
                    <button
                      className="text-left text-[var(--color-db-accent)] hover:opacity-80 font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateHighlightFromInterval(interval.startTime, interval.endTime);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      Create highlight
                    </button>
                    <div
                      className="absolute w-2 h-2"
                      style={{
                        bottom: -5,
                        background: 'var(--editor-sidebar)',
                        borderRight: '1px solid var(--editor-border)',
                        borderBottom: '1px solid var(--editor-border)',
                        left: nearRight ? undefined : nearLeft ? ARROW_INSET - 4 : '50%',
                        right: nearRight ? ARROW_INSET - 4 : undefined,
                        transform: nearLeft || nearRight
                          ? 'rotate(45deg)'
                          : 'translateX(-50%) rotate(45deg)',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Highlighted segments */}
        {slowMotionSegments.map((seg) => {
          if (duration === 0) return null;
          const startPercent = (seg.startTime / duration) * 100;
          const widthPercent = ((seg.endTime - seg.startTime) / duration) * 100;
          const isDraggingThis = draggingSloMoSegmentId === seg.id;
          const isTapped = tappedItemId === `slomo-seg-${seg.id}`;
          return (
            <div
              key={seg.id}
              className={`absolute top-1/2 -translate-y-1/2 h-[12px] group z-20 rounded-full ${isDraggingThis ? 'ring-2 ring-[var(--color-db-oasis)]' : ''}`}
              style={{
                /* Clamped so a marker at either pole sits flush rather than
                   hanging off. The 0.5% minimum width exists so a very short
                   segment is still hittable, but on a segment starting near the
                   very end that minimum used to push the right edge past 100%
                   and the cap overhung the scrubber. Pulling `left` back by the
                   overflow keeps the full rounded cap on screen at both ends. */
                left: `${Math.max(0, Math.min(startPercent, 100 - Math.max(widthPercent, 0.5)))}%`,
                width: `${Math.max(widthPercent, 0.5)}%`,
                background: 'rgba(84, 195, 234, 0.55)',
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                const before = tappedItemId;
                setTappedItemId(isTapped ? null : `slomo-seg-${seg.id}`);
                handleBarTouchDrag(e, () => setTappedItemId(before));
              }}
            >
              {/* Drag handle on right edge */}
              <div
                /* rounded-r-full, not rounded-r-sm. The bar is a 12px pill, so
                   its caps are 6px round; a 2px-radius hover fill squared the
                   right end off and made the two ends read differently for no
                   reason other than this handle living on one of them. The
                   left end has no handle because only the end time is
                   draggable, so symmetry here is about the cap's shape and
                   colour, not about inventing a second grab target. */
                className="absolute right-0 top-0 bottom-0 w-2 bg-transparent group-hover:bg-[var(--color-db-accent-hover)] cursor-ew-resize rounded-r-full touch-none"
                onMouseDown={(e) => handleSloMoSegmentDragStart(e, seg.id)}
                onTouchStart={(e) => handleSloMoSegmentTouchStart(e, seg.id)}
                title="Drag to adjust end time"
              />

              {/* Tooltip on hover (+ tap on mobile).
                  Anchored to the marker's own centre and given a pointer, so it
                  reads as belonging to this segment rather than floating over
                  the timeline. The inline "Seg" chip that used to duplicate
                  this is gone; this is the only thing that names a segment here.

                  Edge clamping is derived from the marker's position rather
                  than measured. A tooltip near either end of the timeline
                  shifts inward and its pointer moves with it, so the box stays
                  on screen and still points at the right place. The 12%
                  thresholds are a heuristic for "about half a tooltip from the
                  edge" — measuring would need a layout pass per marker per
                  hover, which is not worth it for a label. */}
              {(() => {
                const centrePercent = startPercent + Math.max(widthPercent, 0.5) / 2;
                const nearLeft = centrePercent < 12;
                const nearRight = centrePercent > 88;
                /* How far the pointer sits from the shifted tooltip's edge. */
                const ARROW_INSET = 12;
                return (
                  <div
                    /* Phone (spec 13.4): never from hover or a tap. iOS keeps
                       :hover on the bar after a touch, which left this label
                       up, clipped, with nothing being dragged. The phone's
                       label is SegmentDragTip, during a drag only. */
                    className={`absolute bottom-full left-1/2 z-30 pointer-events-none ${phone ? 'hidden' : isTapped ? 'block' : 'hidden group-hover:block'}`}
                    style={{
                      /* 8, down from 22. The 22 existed to clear a 16px row
                         of hover icons above the marker; those are gone, so
                         the tooltip sits where it should have all along —
                         close enough that the pointer reads as attached. */
                      marginBottom: 8,
                      transform: nearLeft
                        ? `translateX(-${ARROW_INSET}px)`
                        : nearRight
                          ? `translateX(calc(-100% + ${ARROW_INSET}px))`
                          : 'translateX(-50%)',
                    }}
                  >
                    <div
                      className="relative w-max max-w-[240px] text-[var(--editor-text)] text-xs px-2 py-1 rounded shadow-lg [overflow-wrap:anywhere]"
                      style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}
                    >
                      {/* The segment's whole name, wrapping past 240 rather than cut; its times under it. */}
                      <span className="block">{seg.label}</span>
                      <span className="block" style={{ color: 'var(--editor-text-muted)' }}>{formatTime(seg.startTime)} – {formatTime(seg.endTime)}</span>
                      {/* The pointer: a rotated square showing two of its
                          borders, so it reads as a continuation of the box
                          rather than a separate shape sitting under it. */}
                      <div
                        className="absolute w-2 h-2"
                        style={{
                          bottom: -5,
                          background: 'var(--editor-sidebar)',
                          borderRight: '1px solid var(--editor-border)',
                          borderBottom: '1px solid var(--editor-border)',
                          left: nearRight ? undefined : nearLeft ? ARROW_INSET - 4 : '50%',
                          right: nearRight ? ARROW_INSET - 4 : undefined,
                          transform: nearLeft || nearRight
                            ? 'rotate(45deg)'
                            : 'translateX(-50%) rotate(45deg)',
                        }}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Save to library button on hover (+ tap on mobile) */}
              {onSaveSegmentToLibrary && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSaveSegmentToLibrary(seg); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  className={`absolute -top-4 left-1/2 -translate-x-1/2 w-4 h-4 text-blue-500 hover:text-blue-700 items-center justify-center z-30 ${isTapped ? 'flex' : phone ? 'hidden' : 'hidden group-hover:flex'}`}
                  title="Save to player library"
                  aria-label="Save to player library"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
              )}

            </div>
          );
        })}


        {/* Audio Recordings - Purple circle markers (bottom row) */}
        {/* {audioRecordings.map((recording) => {
          const position = duration > 0 ? (recording.timestamp / duration) * 100 : 0;
          const isTapped = tappedItemId === `audio-${recording.id}`;
          return (
            <div
              key={recording.id}
              className="absolute bottom-0 h-1/2 flex items-center pointer-events-none z-20"
              style={{ left: `${position}%` }}
            >
              <div
                className="relative group pointer-events-auto"
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setTappedItemId(isTapped ? null : `audio-${recording.id}`);
                }}
              >
                {/ Circle marker /}
                <div className="w-3 h-3 bg-purple-600 rounded-full border-2 border-white shadow-lg" />

                {/ Tooltip on hover (+ tap on mobile) /}
                <div className={`absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white text-gray-800 text-xs px-2 py-1 rounded shadow-lg border border-gray-300 whitespace-nowrap z-30 ${isTapped ? 'block' : 'hidden group-hover:block'}`}>
                  Audio at {formatTime(recording.timestamp)}
                </div>

                {/ Delete button on hover (+ tap on mobile) /}
                {onDeleteAudioRecording && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteAudioRecording(recording.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-grey-500 rounded-full text-xs font-bold items-center justify-center hover:bg-red-600 z-30 ${isTapped ? 'flex' : 'hidden group-hover:flex'}`}
                    title="Delete audio recording"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })} */}

        {/* Recording segments - red bars at top showing voiceover recordings */}
        {/* {recordingSegments
          .filter((segment) => {
            // Don't render the segment if it's currently being recorded
            const isCurrentlyRecording = isRecording &&
              segment.startTime <= currentTime &&
              segment.endTime >= currentTime;
            return !isCurrentlyRecording;
          })
          .map((segment) => {
            if (duration === 0) return null;

            const startPercent = (segment.startTime / duration) * 100;
            const widthPercent = ((segment.endTime - segment.startTime) / duration) * 100;

            const isTapped = tappedItemId === `segment-${segment.id}`;
            return (
              <div
                key={segment.id}
                className={`absolute top-0 h-2 bg-red-500 group z-timeline-segment hover:h-3 transition-all cursor-pointer ${isTapped ? 'h-3' : ''}`}
                style={{
                  left: `${startPercent}%`,
                  width: `${widthPercent}%`
                }}
                title={`Voiceover: ${formatTime(segment.startTime)} - ${formatTime(segment.endTime)}`}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setTappedItemId(isTapped ? null : `segment-${segment.id}`);
                }}
              >
                {/ Delete button on hover (+ tap on mobile) /}
                {onDeleteSegment && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSegment(segment.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`absolute -top-1 -right-1 w-4 h-4 bg-red-700 text-white rounded-full text-xs font-bold items-center justify-center hover:bg-red-800 shadow-lg z-30 ${isTapped ? 'flex' : 'hidden group-hover:flex'}`}
                    title="Delete voiceover"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })} */}

        {/* Clip masks - green bars at bottom showing what will be extracted */}
        {/* {clipMasks.map((mask) => {
            if (duration === 0) return null;

            const startPercent = (mask.startTime / duration) * 100;
            const widthPercent = ((mask.endTime - mask.startTime) / duration) * 100;
            const isDraggingThis = draggingClipMask?.id === mask.id;

            const isTapped = tappedItemId === `mask-${mask.id}`;
            return (
              <div
                key={mask.id}
                className={`absolute bottom-0 h-2 bg-green-500 group pointer-events-auto z-timeline-mask border border-green-800 rounded-sm ${isDraggingThis ? 'ring-2 ring-green-400' : ''}`}
                style={{
                  left: `${startPercent}%`,
                  width: `${widthPercent}%`
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setTappedItemId(isTapped ? null : `mask-${mask.id}`);
                }}
              >
                {/ Left drag handle /}
                {onUpdateClipMask && (
                  <div
                    className="absolute left-0 top-0 bottom-0 w-2 bg-green-700 cursor-ew-resize hover:bg-green-800 rounded-l-sm touch-none"
                    onMouseDown={(e) => handleClipMaskDragStart(e, mask.id, 'start')}
                    onTouchStart={(e) => handleClipMaskTouchStart(e, mask.id, 'start')}
                    title="Drag to adjust start time"
                  />
                )}

                {/ Right drag handle /}
                {onUpdateClipMask && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-2 bg-green-700 cursor-ew-resize hover:bg-green-800 rounded-r-sm touch-none"
                    onMouseDown={(e) => handleClipMaskDragStart(e, mask.id, 'end')}
                    onTouchStart={(e) => handleClipMaskTouchStart(e, mask.id, 'end')}
                    title="Drag to adjust end time"
                  />
                )}

                {/ Tooltip on hover (+ tap on mobile) /}
                <div className={`absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-white text-gray-800 text-xs px-2 py-1 rounded shadow-lg border border-gray-300 whitespace-nowrap z-30 ${isTapped ? 'block' : 'hidden group-hover:block'}`}>
                  Clip: {formatTime(mask.startTime)} - {formatTime(mask.endTime)} ({formatTime(mask.endTime - mask.startTime)})
                </div>

                {/ Delete button on hover (+ tap on mobile) /}
                {onDeleteClipMask && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClipMask(mask.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={`absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 bg-red-600 text-grey-500 rounded-full text-xs font-bold items-center justify-center hover:bg-red-700 shadow-lg z-30 ${isTapped ? 'flex' : 'hidden group-hover:flex'}`}
                    title="Delete clip mask"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })} */}

        {/* First clip mask boundary indicator */}
        {/* {clipMaskFirstBound !== null && duration > 0 && (() => {
          const boundPercent = (clipMaskFirstBound / duration) * 100;
          const formatTime = (seconds: number) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          };

          return (
            <div
              className="absolute bottom-0 h-full bg-yellow-500 pointer-events-none z-timeline-mask opacity-50"
              style={{
                left: `${boundPercent}%`,
                width: '2px'
              }}
              title={`First bound at ${formatTime(clipMaskFirstBound)}`}
            />
          );
        })()} */}

        {/* Flag-in-progress: start boundary + growing region */}
        {sloMoFirstBound !== null && duration > 0 && (
          <>
            {/* Growing highlighted region from start bound to current playhead */}
            {currentTime > sloMoFirstBound && (
              <div
                className="absolute h-[12px] rounded-full pointer-events-none z-10"
                style={{
                  left: `${(sloMoFirstBound / duration) * 100}%`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: `${((currentTime - sloMoFirstBound) / duration) * 100}%`,
                  animation: 'flagPulse 1.5s ease-in-out infinite',
                }}
              />
            )}
            {/* Start boundary line */}
            <div
              className="absolute pointer-events-none z-20"
              style={{
                left: `${(sloMoFirstBound / duration) * 100}%`,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '3px',
                height: '18px',
                background: 'var(--color-db-oasis)',
                boxShadow: '0 0 8px rgba(84, 195, 234, 0.6)',
                borderRadius: '1px',
              }}
              title={`Flag start at ${formatTime(sloMoFirstBound)}`}
            />
          </>
        )}

        {/* Current recording indicator (if recording) */}
        {/* {isRecording && (() => {
          // Find the active recording segment (the last one that hasn't been stopped yet)
          const activeRecordingSegment = recordingSegments.find(
            seg => seg.startTime <= currentTime && seg.endTime >= currentTime
          );

          if (!activeRecordingSegment || duration === 0) return null;

          const startPercent = (activeRecordingSegment.startTime / duration) * 100;
          const rawWidthPercent = ((currentTime - activeRecordingSegment.startTime) / duration) * 100;
          // Ensure minimum width of 2% so it's always visible
          const widthPercent = Math.max(2, rawWidthPercent);

          return (
            <div
              className="absolute top-0 h-full bg-red-500 pointer-events-none border-2 border-yellow-400 z-timeline-recording opacity-90"
              style={{
                left: `${startPercent}%`,
                width: `${widthPercent}%`
              }}
              title={`Recording: ${startPercent.toFixed(1)}% to ${(startPercent + widthPercent).toFixed(1)}%`}
            >
              <div className="absolute inset-0 flex items-center justify-center text-xs text-grey-500 font-bold">
                REC {rawWidthPercent.toFixed(1)}%
              </div>
            </div>
          );
        })()} */}

        {/* Playhead handle */}
        <div
          className="absolute z-30 pointer-events-none"
          style={{ left: `${playheadPosition}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
        >
          <div
            className="w-[16px] h-[16px] rounded-full transition-transform hover:scale-110"
            style={{ background: 'var(--editor-text)', border: '2px solid rgba(255, 255, 255, 0.5)', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
          />
        </div>

        {/* Time markers - interval adapts to both video duration and available width */}
        {duration > 0 && (() => {
          // Minimum px between label centres to avoid crowding (~50px fits "0:00")
          const MIN_PX_PER_TICK = 56;
          const maxTicks = timelineWidth > 0 ? Math.floor(timelineWidth / MIN_PX_PER_TICK) : 20;
          const candidates = [5, 10, 15, 30, 60, 120, 300, 600];
          const tickInterval = candidates.find(
            (iv) => Math.floor(duration / iv) <= maxTicks
          ) ?? candidates[candidates.length - 1];

          return Array.from({ length: Math.floor(duration / tickInterval) }).map((_, index) => {
            const time = (index + 1) * tickInterval;
            const position = (time / duration) * 100;

            return (
              <div
                key={time}
                className="absolute top-1/2 -translate-y-1/2 h-[12px] w-px bg-[var(--editor-text-muted)]/10 pointer-events-none"
                style={{ left: `${position}%` }}
              />
            );
          });
        })()}
      </div>

      {/* Legend */}
      {/* <div className="flex gap-4 mt-6 text-xs text-gray-600">
        {annotationSnapshots.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-6 h-3 bg-blue-500/60 rounded-sm border border-blue-600" />
            <span>Annotations ({annotationSnapshots.length}) - drag right edge to adjust duration</span>
          </div>
        )}
        {audioRecordings.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-purple-600 rounded-full border border-white" />
            <span>Audio recordings ({audioRecordings.length})</span>
          </div>
        )}
        {recordingSegments.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-400/60 rounded" />
            <span>Voiceover segments ({recordingSegments.length})</span>
          </div>
        )}
        {clipMasks.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-400/30 rounded border border-green-700" />
            <span>Clip masks ({clipMasks.length}) - drag edges to resize</span>
          </div>
        )}
        {isRecording && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded animate-pulse" />
            <span>Recording...</span>
          </div>
        )}
        {isPausedAudioRecording && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-pulse" />
            <span>Recording audio (paused)...</span>
          </div>
        )}
        {clipMaskFirstBound !== null && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-500 rounded animate-pulse" />
            <span>Setting clip mask boundary...</span>
          </div>
        )}
        {slowMotionSegments.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-6 h-[5px] bg-db-accent/70 rounded-sm border border-db-accent" />
            <span>Notable segments ({slowMotionSegments.length}) - drag right edge to adjust</span>
          </div>
        )}
        {sloMoFirstBound !== null && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-db-accent rounded animate-pulse" />
            <span>Setting notable segment boundary...</span>
          </div>
        )}
      </div> */}
    </div>
  );
}

/**
 * Phone (spec 13.4): a segment's label during a drag, portalled to the page
 * and placed in screen coordinates, so no ancestor clips it. Centred over the
 * segment and clamped 8 inside the visible width; above the bar.
 */
function SegmentDragTip({ trackRef, centrePercent, text }: { trackRef: React.RefObject<HTMLDivElement | null>; centrePercent: number; text: string }) {
  const tipRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const tip = tipRef.current;
    const trackEl = trackRef.current;
    if (!tip || !trackEl) return;
    const r = trackEl.getBoundingClientRect();
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const cx = r.left + (r.width * centrePercent) / 100;
    tip.style.left = `${Math.max(8, Math.min(cx - tip.offsetWidth / 2, vw - 8 - tip.offsetWidth))}px`;
    tip.style.top = `${Math.max(8, r.top + r.height / 2 - 6 - 8 - tip.offsetHeight)}px`;
  });
  return createPortal(
    <div
      ref={tipRef}
      className="fixed w-max max-w-[240px] pointer-events-none text-xs px-2 py-1 rounded shadow-lg [overflow-wrap:anywhere]"
      style={{ left: -9999, top: -9999, zIndex: 'var(--z-editor-menu)', color: 'var(--editor-text)', background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}
    >
      {text}
    </div>,
    document.body,
  );
}
